"use client";

import { motion } from "framer-motion";
import { useRouter } from "next/router"; // ✅ untuk folder /pages

const formatPriceDot = (value) => {
  const num = Number(value);
  if (!isFinite(num)) return "0.00";
  const sign = num < 0 ? "-" : "";
  const n = Math.abs(num);
  const [int, dec] = n.toFixed(2).split(".");
  const intWithDots = int.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${sign}${intWithDots}.${dec}`;
};

const StarRating = ({ value = 4.5 }) => {
  const full = Math.floor(value);
  const half = value % 1 >= 0.5;

  const stars = [];
  for (let i = 0; i < full; i++) {
    stars.push(
      <span key={`f-${i}`} className="w-4 h-4 text-yellow-500">
        ★
      </span>
    );
  }
  if (half) {
    stars.push(
      <span key="half" className="w-4 h-4 text-yellow-500 opacity-70">
        ★
      </span>
    );
  }
  while (stars.length < 5) {
    stars.push(
      <span key={`e-${stars.length}`} className="w-4 h-4 text-gray-300">
        ★
      </span>
    );
  }

  return <div className="flex">{stars}</div>;
};

export default function ProductCard({ product, href }) {
  const router = useRouter();

  const stock = Number(product?.stock) || 0;

  const getFrontImage = () => {
    if (Array.isArray(product?.images) && product.images.length > 0) {
      const front = product.images.find((img) => img?.type === "front");
      return front?.url || product.images[0]?.url;
    }
    return "/product-placeholder.jpg";
  };

  const handleAddToCart = () => {
    try {
      if (stock <= 0) {
        alert("Maaf, stok habis.");
        return;
      }

      const raw = localStorage.getItem("cart") || "[]";
      const cart = JSON.parse(raw);
      if (!Array.isArray(cart)) throw new Error("Cart is not array");

      const existing = cart.find((item) => item.id === product.id);

      if (existing) {
        const currentQty = Number(existing.quantity || 0);

        if (currentQty >= stock) {
          alert(`Maksimal pembelian untuk produk ini adalah ${stock} pcs.`);
          return;
        }

        existing.quantity = currentQty + 1;
      } else {
        cart.push({
          id: product.id,
          name: product.name,
          price: product.price,
          image: getFrontImage(),
          quantity: 1,
          store: {
            id: product.vendor?.id ?? "unknown",
            name: product.vendor?.storeName ?? "Unknown Store",
            phone: product.vendor?.phone ?? "",
          },
        });
      }

      localStorage.setItem("cart", JSON.stringify(cart));
      window.dispatchEvent(new Event("cartUpdated"));
    } catch (e) {
      console.error("Cart error:", e);
    }
  };

  const goDetail = () => {
    if (!href) return;
    router.push(href);
  };

  return (
    <motion.div
      whileHover={{ y: -4 }}
      onClick={goDetail}
      role={href ? "button" : undefined}
      className="group rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-transform transition-shadow border border-zinc-100 dark:border-zinc-800 bg-white dark:bg-zinc-900 cursor-pointer"
      style={{ borderTop: "4px solid #003B5C" }}
    >
      <div className="relative">
        <img
          src={getFrontImage()}
          alt={product?.name || "Product"}
          className="w-full h-64 object-cover"
        />

        {stock <= 0 && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <span className="text-white font-bold bg-red-500 px-2 py-1 rounded">
              Out of Stock
            </span>
          </div>
        )}
      </div>

      <div className="p-4 space-y-2 flex flex-col h-full">
        <h3 className="text-sm sm:text-base font-medium line-clamp-1 hover:underline">
          {product?.name}
        </h3>

        <StarRating value={product?.rating || 4.5} />

        <div
          className="text-xs text-zinc-500 dark:text-zinc-400 prose max-h-[3rem] overflow-hidden"
          dangerouslySetInnerHTML={{
            __html: product?.description || "<p>No description available</p>",
          }}
        />

        <div className="flex items-center justify-between pt-2 gap-2 mt-auto">
          <div>
            <span className="text-lg font-semibold text-[#00778B]">
              Rp{formatPriceDot(product?.price)}
            </span>

            {stock > 0 && (
              <span className="text-xs text-green-600 dark:text-green-400 ml-2">
                {stock} in stock
              </span>
            )}
          </div>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation(); // ✅ penting: jangan ikut navigate
              handleAddToCart();
            }}
            className="px-3 py-1.5 text-xs rounded-full border border-[#00778B] text-[#00778B] hover:bg-[#00778B] hover:text-white transition"
          >
            Add to Cart
          </button>
        </div>
      </div>
    </motion.div>
  );
}